// PadView — the pad on the workbench (Oct 28 2026): the page of commands and steps the EmCog keeps beside the
// conversation, live as it changes (each fenced block with its Copy button), and editable here — the person's version is
// the one that counts. An edit made from a page that changed meanwhile isn't saved over the change without asking.
// Every change keeps the page as it was: the history shows the earlier pages, and any of them can be put back.
import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../contexts/AuthContext';
import { CodeBlock } from '../MessageBubble';

export interface PadData {
  id: string;
  title: string;
  content: string;
  createdAt: number;
  updatedAt: number;
  by: 'me' | 'person';
  version: number;
  /** How many earlier pages are kept. */
  versions?: number;
}

interface PadVersion { version: number; title: string; content: string; by: 'me' | 'person'; at: number }

interface Props {
  pad: PadData; emcogName: string; editing: boolean; onEditing: (on: boolean) => void; onSaved: (pad: PadData) => void;
  /** The earlier pages, instead of the page. */
  history: boolean; onHistory: (on: boolean) => void;
}

const markdown = (content: string) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ pre: ({ children }: any) => <CodeBlock>{children}</CodeBlock> }}>{content}</ReactMarkdown>
);

function whenShort(at: number): string {
  const mins = Math.round((Date.now() - at) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return new Date(at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const PadHistory: React.FC<{ pad: PadData; emcogName: string; onSaved: (pad: PadData) => void; onDone: () => void }> = ({ pad, emcogName, onSaved, onDone }) => {
  const { token } = useAuth();
  const [versions, setVersions] = useState<PadVersion[] | null>(null);
  const [picked, setPicked] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/workspace/pads/${encodeURIComponent(pad.id)}/history`, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.ok) throw new Error(out.error || `The history didn't open (${res.status}).`);
      setVersions(out.versions);
      setPicked(p => (p !== null && out.versions.some((v: PadVersion) => v.version === p) ? p : out.versions[0]?.version ?? null));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [pad.id, token]);
  // Opened, and again whenever the pad changes underneath (a change adds the page it replaced).
  useEffect(() => { load(); }, [load, pad.version]);

  const putBack = async (version: number) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`/api/workspace/pads/${encodeURIComponent(pad.id)}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ version, base: pad.version })
      });
      const out = await res.json().catch(() => ({}));
      if (res.status === 409) { setError(`The pad changed just now — here's its history as it is now.`); await load(); return; }
      if (!res.ok || !out.ok) throw new Error(out.error || `It wasn't put back (${res.status}).`);
      onSaved(out.pad);
      onDone();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  if (!versions) return <div className="ws-empty">{error || 'Opening the history…'}</div>;
  if (!versions.length) return <div className="ws-empty">No earlier pages yet — this is the pad as it was first written.</div>;
  const shown = versions.find(v => v.version === picked) || versions[0];
  return (
    <div className="ws-pad-hist">
      <div className="ws-pad-hist-list" role="listbox" aria-label="Earlier pages">
        {versions.map(v => (
          <button key={v.version} type="button" role="option" aria-selected={v.version === shown.version} className={v.version === shown.version ? 'on' : ''} onClick={() => setPicked(v.version)}>
            <b>Version {v.version}</b>
            <span>{whenShort(v.at)} · {v.by === 'person' ? 'you' : emcogName}</span>
          </button>
        ))}
      </div>
      <div className="ws-pad-hist-page">
        <div className="ws-pad-hist-acts">
          <span>Version {shown.version}{shown.title !== pad.title ? ` — "${shown.title}"` : ''}, as it was {whenShort(shown.at)}</span>
          <button type="button" className="ws-btn ws-primary" onClick={() => putBack(shown.version)} disabled={busy}>{busy ? 'Putting it back…' : 'Put this version back'}</button>
        </div>
        {error && <div className="ws-pad-error">{error}</div>}
        <div className="ws-doc ws-pad">{markdown(shown.content)}</div>
      </div>
    </div>
  );
};

const PadView: React.FC<Props> = ({ pad, emcogName, editing, onEditing, onSaved, history, onHistory }) => {
  const { token } = useAuth();
  const [draft, setDraft] = useState(pad.content);
  const [base, setBase] = useState(pad.version);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The page changed while it was being edited: whose version wins is the person's call.
  const [changed, setChanged] = useState<PadData | null>(null);

  // A fresh edit starts from the page as it is now.
  useEffect(() => { if (editing) { setDraft(pad.content); setBase(pad.version); setChanged(null); setError(null); } }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps
  // While editing, a change from elsewhere is noted, never poured over the draft (this screen's own save isn't one).
  useEffect(() => { if (editing && !saving && pad.version !== base && !changed) setChanged(pad); }, [pad, editing, base, changed, saving]);

  const save = async (version: number) => {
    setSaving(true); setError(null);
    try {
      const res = await fetch(`/api/workspace/pads/${encodeURIComponent(pad.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ content: draft, version })
      });
      const out = await res.json().catch(() => ({}));
      if (res.status === 409 && out.conflict) { setChanged(out.conflict); return; }
      if (!res.ok || !out.ok) throw new Error(out.error || `It didn't save (${res.status}).`);
      onSaved(out.pad);
      onEditing(false);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  if (history) return <PadHistory pad={pad} emcogName={emcogName} onSaved={onSaved} onDone={() => onHistory(false)} />;
  if (editing) {
    return (
      <div className="ws-pad-edit">
        {changed && (
          <div className="ws-pad-conflict" role="alert">
            <span>{changed.by === 'me' ? emcogName : 'Another screen'} changed this pad while you were editing.</span>
            <div className="ws-pad-conflict-acts">
              <button type="button" className="ws-btn" onClick={() => { setDraft(changed.content); setBase(changed.version); setChanged(null); }}>Use the new version</button>
              <button type="button" className="ws-btn" onClick={() => { const v = changed.version; setBase(v); setChanged(null); save(v); }}>Save mine over it</button>
            </div>
          </div>
        )}
        <label className="ws-sr" htmlFor="ws-pad-text">The pad's text</label>
        <textarea id="ws-pad-text" className="ws-pad-text" value={draft} onChange={e => setDraft(e.target.value)} spellCheck={false} autoFocus />
        {error && <div className="ws-pad-error">{error}</div>}
        <div className="ws-pad-edit-acts">
          <button type="button" className="ws-btn ws-primary" onClick={() => save(base)} disabled={saving || !!changed}>{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" className="ws-btn" onClick={() => onEditing(false)} disabled={saving}>Cancel</button>
        </div>
      </div>
    );
  }
  return <div className="ws-doc ws-pad">{markdown(pad.content)}</div>;
};

export default PadView;
